import { windowCrossLevelCutPlane } from "building-generation/elements/window/windowUtils";
import type { Level } from "building-types/levels/levelTypes";
import type { PlanarGraphLevelAssociations } from "building-types/planarGraphs/planarGraphAssociationTypes";
import type { GeneratedWindowPlacement, WindowUnit } from "building-types/windows/windowTypes";
import { FLOOR_PLAN_CUT_PLANE } from "common/constants/buildingObjects";
import type { Table } from "common/data/table";
import objectMap from "common/utils/objectMap";

export function windowsCrossLevelCutPlane(
  levels: Table<Level>,
  levelId: string,
  windows: Table<GeneratedWindowPlacement>,
  cutPlaneHeight?: number,
  planarGraphLevelAssociations?: Table<PlanarGraphLevelAssociations>,
): GeneratedWindowPlacement[] {
  const validWindowKeys = windowIdsCrossLevelCutPlane(
    levels,
    levelId,
    windows,
    cutPlaneHeight,
    planarGraphLevelAssociations,
  );

  return validWindowKeys.map((key) => windows[key]);
}

export function windowUnitsCrossLevelCutPlane(
  levels: Table<Level>,
  levelId: string,
  windows: Table<WindowUnit>,
  cutPlaneHeight?: number,
  planarGraphLevelAssociations?: Table<PlanarGraphLevelAssociations>,
): WindowUnit[] {
  const windowPlacements = objectMap(windows, (windowUnit) => windowUnit.windowPlacement);

  const validWindowKeys = windowIdsCrossLevelCutPlane(
    levels,
    levelId,
    windowPlacements,
    cutPlaneHeight,
    planarGraphLevelAssociations,
  );

  return validWindowKeys.map((key) => windows[key]);
}

function windowIdsCrossLevelCutPlane(
  levels: Table<Level>,
  levelId: string,
  windows: Table<GeneratedWindowPlacement>,
  cutPlaneHeight?: number,
  planarGraphLevelAssociations?: Table<PlanarGraphLevelAssociations>,
): string[] {
  const windowKeys: string[] = [];
  const level = levels[levelId];
  const levelAboveId = level.levelAbove;
  const levelBelowId = level.levelBelow;

  const cutPlaneElevation = level.floorElevation + (cutPlaneHeight ?? FLOOR_PLAN_CUT_PLANE);
  const levelAboveElevation = levelAboveId ? levels[levelAboveId]?.floorElevation : undefined;
  const levelBelowElevation = levelBelowId ? levels[levelBelowId]?.floorElevation : undefined;

  for (const id in windows) {
    const window = windows[id];

    const crossCutPlane = windowCrossLevelCutPlane(
      window,
      levelAboveId,
      levelAboveElevation,
      levelBelowId,
      levelBelowElevation,
      cutPlaneElevation,
    );
    if (!crossCutPlane) {
      continue;
    }

    // For backwards compatibility, only check planarGraph associations if cutPlaneHeight is passed
    if (
      cutPlaneHeight !== undefined &&
      planarGraphLevelAssociations &&
      !windowHasWallOnLevel(window, levelAboveId, levelBelowId, planarGraphLevelAssociations)
    ) {
      continue;
    }

    windowKeys.push(id);
  }
  return windowKeys;
}

/**
 * Checks whether a cross-level window's host wall has a corresponding wall segment
 * on the current level via planar graph half-edge associations. When levels use
 * different datums, a window may geometrically cross the cut plane without having
 * any wall continuity on the current level.
 */
function windowHasWallOnLevel(
  window: GeneratedWindowPlacement,
  levelAboveId: string | undefined,
  levelBelowId: string | undefined,
  associations: Table<PlanarGraphLevelAssociations>,
): boolean {
  // frontRoom is undefined for roof/dormer windows, which have no wall on any level
  const windowLevel = window.frontRoom?.level.id;

  if (levelAboveId && windowLevel === levelAboveId) {
    const corresponding = associations[levelAboveId]?.halfEdgesBelow[window.halfEdgeIndex];
    return corresponding !== undefined && corresponding.length > 0;
  }

  if (levelBelowId && windowLevel === levelBelowId) {
    const corresponding = associations[levelBelowId]?.halfEdgesAbove[window.halfEdgeIndex];
    return corresponding !== undefined && corresponding.length > 0;
  }

  return false;
}
